import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import {
  useAdvanceOutboundBenchOrdersMutation,
  usePlanOutboundBenchRunLazyQuery,
  type BenchRunReportFieldsFragment,
  type PlanOutboundBenchRunInput,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import {
  freezeRunSelection,
  nextStepSummary,
  runReportCursor,
  secondsUntil,
  shouldContinueRun,
} from "../bench-run";

const styles = {
  controls: {
    display: "flex",
    gap: 2,
    alignItems: "center",
    flexWrap: "wrap",
  } satisfies SxProps<Theme>,
  countdown: { alignItems: "center" } satisfies SxProps<Theme>,
};

/** Default pause between lifecycle steps. */
const DEFAULT_DELAY_SECONDS = "50";

/** The page never calls faster than this, which keeps it under the 30/min throttle. */
const MIN_DELAY_MS = 3000;

/** Why a production run stopped when the operator cleared the typed `prod`. */
export const CONFIRMATION_CLEARED_MESSAGE =
  "Stopped: the production confirmation was cleared. Type prod and run again to continue.";

/** How often the countdown to the next round redraws. */
const COUNTDOWN_TICK_MS = 1000;

type UpcomingRound = {
  /** What the next round will run, or null when no order has a step ready. */
  summary: string | null;
  /** When the next round starts, in epoch milliseconds. */
  at: number;
};

/**
 * The next round's steps and a countdown to it. It ticks on its own, so the
 * panel does not re-render every second.
 */
const NextRoundCountdown = memo(
  ({ upcoming }: { readonly upcoming: UpcomingRound }) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
      const timer = setInterval(() => {
        setNow(Date.now());
      }, COUNTDOWN_TICK_MS);
      return () => {
        clearInterval(timer);
      };
    }, []);
    return (
      <Alert severity="info" sx={styles.countdown}>
        <Typography fontWeight={600}>
          Next step: {upcoming.summary ?? "none ready"}
        </Typography>
        <Typography variant="body2">
          Runs in {secondsUntil(upcoming.at, now)}s
        </Typography>
      </Alert>
    );
  },
);
NextRoundCountdown.displayName = "NextRoundCountdown";

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

type ScenarioPanelProps = {
  readonly input: PlanOutboundBenchRunInput | null;
  readonly disabled: boolean;
  readonly confirmProductionSend: boolean | undefined;
  readonly onDryRunReport: (report: BenchRunReportFieldsFragment) => void;
  readonly onRunReport: (report: BenchRunReportFieldsFragment) => void;
  /** The error that ended a run, or null when a run starts. */
  readonly onRunError: (message: string | null) => void;
};

/**
 * Scenario mode. Dry run shows each order's plan and pre-flight. Run calls
 * the advance mutation once per round, one step per order, waiting the delay
 * between rounds, until no order's last step ran, one failed, or the operator
 * stops it. Every round after the first names the orders the first round
 * resolved. The two report through separate callbacks, so the page keeps the
 * plan apart from the run's results, and a run's error goes to the page's
 * error area above those results.
 *
 * Each round reads the current `confirmProductionSend`, so clearing the typed
 * `prod` stops a production run before its next round. The page remounts the
 * panel on a partnership change, which stops the run and drops the results of
 * a round still in flight.
 */
const ScenarioPanel = ({
  input,
  disabled,
  confirmProductionSend,
  onDryRunReport,
  onRunReport,
  onRunError,
}: ScenarioPanelProps) => {
  const [delaySeconds, setDelaySeconds] = useState(DEFAULT_DELAY_SECONDS);
  const [running, setRunning] = useState(false);
  const [round, setRound] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingRound | null>(null);
  const stopRequested = useRef(false);
  const unmounted = useRef(false);
  const confirmRef = useRef(confirmProductionSend);
  useEffect(() => {
    confirmRef.current = confirmProductionSend;
  }, [confirmProductionSend]);
  // The internal app's watch queries default to `errorPolicy: "ignore"`,
  // which would drop a refused dry run and leave the previous report showing.
  const [plan, { loading: planning }] = usePlanOutboundBenchRunLazyQuery({
    fetchPolicy: "no-cache",
    errorPolicy: "none",
  });
  const [advance] = useAdvanceOutboundBenchOrdersMutation();

  // Leaving Scenario mode or the page ends the run; a request already sent
  // still finishes.
  useEffect(
    () => () => {
      stopRequested.current = true;
      unmounted.current = true;
    },
    [],
  );

  const handleDelayChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDelaySeconds(event.target.value);
    },
    [],
  );

  const runDryRun = useCallback(async () => {
    if (input === null) {
      return;
    }
    setError(null);
    try {
      const result = await plan({ variables: { input } });
      throwOnGraphqlErrors(result);
      if (result.data !== undefined) {
        onDryRunReport(result.data.planOutboundBenchRun);
      }
    } catch (dryRunError) {
      setError(
        dryRunError instanceof Error ? dryRunError.message : "Dry run failed.",
      );
    }
  }, [input, onDryRunReport, plan]);

  const runScenario = useCallback(async () => {
    if (input === null) {
      return;
    }
    setError(null);
    onRunError(null);
    setRunning(true);
    stopRequested.current = false;
    const startedConfirmed = confirmRef.current === true;
    const parsedSeconds = Number.parseFloat(delaySeconds);
    const delayMs = Math.max(
      MIN_DELAY_MS,
      Number.isFinite(parsedSeconds) ? parsedSeconds * 1000 : 0,
    );
    const step = async (
      nextRound: number,
      roundInput: PlanOutboundBenchRunInput,
      reportEdiSince: string | undefined,
    ): Promise<void> => {
      if (stopRequested.current) {
        return;
      }
      const confirmed = confirmRef.current;
      if (startedConfirmed && confirmed !== true) {
        onRunError(CONFIRMATION_CLEARED_MESSAGE);
        return;
      }
      setUpcoming(null);
      setRound(nextRound);
      const result = await advance({
        variables: {
          input: {
            ...roundInput,
            confirmProductionSend: confirmed,
            reportEdiSince,
          },
        },
      });
      if (unmounted.current) {
        return;
      }
      throwOnGraphqlErrors(result);
      const report = result.data?.advanceOutboundBenchOrders;
      if (report === undefined) {
        throw new Error("The mutation returned no report.");
      }
      onRunReport(report);
      if (!shouldContinueRun(report) || stopRequested.current) {
        return;
      }
      setUpcoming({
        summary: nextStepSummary(report),
        at: Date.now() + delayMs,
      });
      await sleep(delayMs);
      await (nextRound === 1
        ? step(
            nextRound + 1,
            freezeRunSelection(roundInput, report),
            runReportCursor(report),
          )
        : step(nextRound + 1, roundInput, reportEdiSince));
    };
    try {
      // The first round leaves the cursor to the server, which reports from
      // each step's own start.
      await step(1, input, undefined);
    } catch (runError) {
      if (!unmounted.current) {
        onRunError(
          runError instanceof Error ? runError.message : "Run failed.",
        );
      }
    } finally {
      setUpcoming(null);
      setRunning(false);
    }
  }, [advance, delaySeconds, input, onRunError, onRunReport]);

  const handleDryRunClick = useCallback(() => {
    void runDryRun();
  }, [runDryRun]);
  const handleRunClick = useCallback(() => {
    void runScenario();
  }, [runScenario]);
  const handleStopClick = useCallback(() => {
    stopRequested.current = true;
  }, []);

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        Each round runs the next lifecycle step of every selected order through
        the service the TMS calls for it: tender accept, appointment confirm,
        dispatch, arrive, complete, line haul, on hand, finalize, invoice, post
        and the 210. Steps already done are skipped, so a run can be resumed.
      </Typography>
      <Box sx={styles.controls}>
        <TextField
          label="Delay between steps (seconds)"
          type="number"
          value={delaySeconds}
          onChange={handleDelayChange}
        />
        <Button
          disabled={input === null || planning || running}
          variant="outlined"
          onClick={handleDryRunClick}
        >
          Dry run
        </Button>
        <Button
          disabled={input === null || disabled || running}
          variant="contained"
          onClick={handleRunClick}
        >
          Run lifecycle
        </Button>
        <Button color="warning" disabled={!running} onClick={handleStopClick}>
          Stop after this step
        </Button>
        {running && <Typography variant="body2">Round {round}</Typography>}
      </Box>
      {running && upcoming !== null && (
        <NextRoundCountdown key={upcoming.at} upcoming={upcoming} />
      )}
      {error !== null && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};

export default ScenarioPanel;
